import React from "react";
import { LocateFixed } from "lucide-react";
import { AttendanceMethodNames, RoleEnum } from "../../../types/common-types";
import { useAuthStore } from "../../../store/auth-store";

export enum LeaveType {
  CL = "CL",
  SL = "SL",
  EL = "EL",
  LWP = "LWP",
}

export interface PunchData {
  time: string;
  source: AttendanceMethodNames;
}

export interface LeaveData {
  type: string;
  color: string;
}

export interface AttendanceCellData {
  inPunch?: PunchData;
  outPunch?: PunchData;

  inLeave?: LeaveData;
  outLeave?: LeaveData;

  fullDayLeave?: LeaveData;
}

interface AttendanceCellProps {
  data?: AttendanceCellData;
  className?: string;
  onClick?: (value: "inLocation" | "outLocation") => void;
}

const AttendanceCell: React.FC<AttendanceCellProps> = ({
  data,
  className = "",
  onClick = () => {}
}) => {
  if (!data) {
    return (
      <div
        className={`flex items-center justify-center text-gray-500 ${className}`}
      >
        -
      </div>
    );
  }

  if (data.fullDayLeave) {
    return (
      <div className={`flex items-center justify-center ${className}`}>
        <LeaveBadge leave={data.fullDayLeave} />
      </div>
    );
  }

  return (
    <div className={`grid grid-cols-7 gap-1 ${className}`}>
      <div className={`flex flex-col col-span-3 ${data.inLeave ? "items-center" : "items-end"} justify-center gap-1 min-w-[120px]`}>
        {data.inLeave && <LeaveBadge leave={data.inLeave} />}

        {!data.inLeave && data.inPunch && <PunchInfo onClick={() => onClick("inLocation")} punch={data.inPunch} />}
        {data.outLeave && data.outPunch && <PunchInfo onClick={() => onClick("outLocation")} punch={data.outPunch} />}
      </div>

      <span className="font-medium col-span-1 flex items-center justify-center text-gray-600">-</span>

      <div className={`flex flex-col col-span-3 ${data.outLeave ? "items-center" : "items-end"} justify-center gap-1 min-w-[120px]`}>
        {data.inLeave && data.inPunch && <PunchInfo onClick={() => onClick("inLocation")} punch={data.inPunch} />}
        {!data.outLeave && data.outPunch && <PunchInfo onClick={() => onClick("outLocation")} punch={data.outPunch} />}

        {data.outLeave && <LeaveBadge leave={data.outLeave} />}
      </div>
    </div>
  );
};

export default AttendanceCell;

interface PunchInfoProps {
  punch: PunchData;
  onClick: () => void;
}

const PunchInfo: React.FC<PunchInfoProps> = ({ punch, onClick }) => {
  const {user} = useAuthStore();
  const isEmployee = user.role === RoleEnum.EMPLOYEE;
  return (
    <div className="text-center flex items-center gap-2">
      <div className="font-medium text-sm leading-none border-r border-secondary/50 pr-2">
        {punch.time}
      </div>

      <div className="flex items-center justify-center gap-1 text-[10px] text-gray-500">
        <span className="capitalize">{punch.source}</span>

        {!isEmployee && <LocateFixed size={15} className="text-indigo-500 cursor-pointer" onClick={onClick} strokeWidth={2} />}
      </div>
    </div>
  );
};

interface LeaveBadgeProps {
  leave: LeaveData;
}

const LeaveBadge: React.FC<LeaveBadgeProps> = ({ leave }) => {
  return (
    <div
      className={`px-8 flex items-center justify-center font-medium text-md bg-white border-[1.5px] ${leave?.color}`}
    >
      {leave.type}
    </div>
  );
};
